import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, doc, onSnapshot } from '../services/firebase';

/**
 * Announcement Banner — Reads from Firestore settings/whapaxx → announcement.
 * Sits above the Navbar. Supports: text, link, link label, accent color, dismissible.
 *
 * Firestore shape:
 * {
 *   announcement: {
 *     active: boolean,
 *     text: string,
 *     link: string,        // optional URL
 *     linkLabel: string,    // optional e.g. "Learn More"
 *     color: string,        // accent hex e.g. '#ffc857'
 *     dismissible: boolean, // can user close it
 *   }
 * }
 */

const DISMISS_KEY = 'wpxg_banner_dismissed';

export default function AnnouncementBanner({ onHeightChange }) {
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [height, setHeight] = useState(0);
  const ref = React.useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'whapaxx'), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (data.announcement && data.announcement.active && data.announcement.text) {
        const stored = sessionStorage.getItem(DISMISS_KEY);
        // Reset dismiss if text changed
        if (stored && stored !== data.announcement.text) {
          sessionStorage.removeItem(DISMISS_KEY);
          setDismissed(false);
        } else if (stored) {
          setDismissed(true);
        }
        setBanner(data.announcement);
      } else {
        setBanner(null);
      }
    }, (err) => {
      console.warn('[AnnouncementBanner] Firestore error:', err);
    });
    return () => unsub();
  }, []);

  // Measure height and report to parent
  useEffect(() => {
    if (!ref.current || !banner || dismissed) {
      if (height !== 0) {
        setHeight(0);
        onHeightChange?.(0);
      }
      return;
    }
    const measure = () => {
      const h = ref.current?.offsetHeight || 0;
      if (h !== height) {
        setHeight(h);
        onHeightChange?.(h);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [banner, dismissed, onHeightChange, height]);

  if (!banner || dismissed) return null;

  const color = banner.color || '#12d0ff';

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, banner.text);
    setDismissed(true);
    onHeightChange?.(0);
  };

  return (
    <div ref={ref} style={{
      ...styles.banner,
      background: `linear-gradient(90deg, ${color}30, ${color}12, ${color}30)`,
      borderBottom: `2px solid ${color}60`,
      boxShadow: `0 2px 20px ${color}25, inset 0 1px 0 ${color}20`,
    }}>
      {/* Animated shimmer overlay */}
      <div style={styles.shimmer} />
      {/* Pulsing edge glow lines */}
      <div style={{ ...styles.edgeLine, left: 0, background: `linear-gradient(90deg, ${color}80, transparent)` }} />
      <div style={{ ...styles.edgeLine, right: 0, background: `linear-gradient(270deg, ${color}80, transparent)` }} />

      <div style={styles.inner}>
        <span style={styles.megaphone}>📢</span>
        <span style={{ ...styles.text, color: '#eaf2ff', textShadow: `0 0 10px ${color}60` }}>
          {banner.text}
        </span>
        {banner.link && (
          banner.link.startsWith('http') ? (
            <a
              href={banner.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...styles.link, background: color, color: '#05081f', boxShadow: `0 0 12px ${color}50` }}
            >
              {banner.linkLabel || 'Learn More'} →
            </a>
          ) : (
            <button
              onClick={() => {
                const [path, hash] = banner.link.split('#');
                navigate(path || '/events');
                if (hash) {
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('openEvent', { detail: hash }));
                  }, 150);
                }
              }}
              style={{ ...styles.link, background: color, color: '#05081f', boxShadow: `0 0 12px ${color}50`, border: 'none', cursor: 'pointer' }}
            >
              {banner.linkLabel || 'View Event'} →
            </button>
          )
        )}
      </div>
      {banner.dismissible !== false && (
        <button onClick={handleDismiss} style={styles.close} aria-label="Dismiss">
          ✕
        </button>
      )}

      <style>{`
        @keyframes bannerShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes bannerEdgePulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>

    </div>
  );
}

const styles = {
  banner: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
    padding: '0.6rem 1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(20px)',
    overflow: 'hidden',
  },
  shimmer: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 40%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 60%, transparent 100%)',
    animation: 'bannerShimmer 3s ease-in-out infinite',
    pointerEvents: 'none',
  },
  edgeLine: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '60px',
    animation: 'bannerEdgePulse 2s ease-in-out infinite',
    pointerEvents: 'none',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap',
    flex: 1,
    textAlign: 'center',
    position: 'relative',
    zIndex: 1,
  },
  megaphone: {
    fontSize: '0.95rem',
    flexShrink: 0,
  },
  text: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '0.9rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
  },
  link: {
    fontFamily: "'Orbitron', sans-serif",
    fontSize: '0.6rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    textDecoration: 'none',
    padding: '0.3rem 0.75rem',
    border: 'none',
    borderRadius: '3px',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    position: 'relative',
    zIndex: 1,
  },
  close: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    fontSize: '1rem',
    cursor: 'pointer',
    padding: '0.4rem 0.6rem',
    borderRadius: '6px',
    color: '#eaf2ff',
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,
    lineHeight: 1,
    minWidth: '32px',
    minHeight: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};
